// v229 admin client for the CNPG ImageCatalog mirror (item 46 of
// PRODUCT-ROADMAP.md, "CNPG ImageCatalog-Mirror"; item 52 in the
// recommended delivery order).

import useSWR from 'swr';

export interface CNPGSettings {
  enabled: boolean;
  sync_interval_sec: number;
  upstream_image: string;
  approved_majors: number[];
  mirror_image_base: string;
  catalog_name: string;
  last_sync_at?: string | null;
  last_sync_status: string;
  last_sync_error: string;
  last_sync_count: number;
  updated_at: string;
  updated_by?: string;
}

export interface CNPGCatalogRow {
  id: string;
  major: number;
  full_version: string;
  upstream_image: string;
  upstream_digest: string;
  approved: boolean;
  eol_date?: string | null;
  synced_at: string;
}

export interface CNPGCatalogResponse {
  rows: CNPGCatalogRow[];
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

const text = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.text();
  });

export function useCNPGSettings() {
  return useSWR<CNPGSettings>('/api/admin/cnpg/settings', json, {
    refreshInterval: 15_000,
  });
}

export function useCNPGCatalog() {
  return useSWR<CNPGCatalogResponse>('/api/admin/cnpg/catalog', json, {
    refreshInterval: 30_000,
  });
}

export function useCNPGYAML() {
  return useSWR<string>('/api/admin/cnpg/yaml', text, {
    refreshInterval: 30_000,
  });
}

export async function updateCNPGSettings(
  input: Omit<
    CNPGSettings,
    'last_sync_at' | 'last_sync_status' | 'last_sync_error' | 'last_sync_count' | 'updated_at' | 'updated_by'
  >,
): Promise<void> {
  const res = await fetch('/api/admin/cnpg/settings', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function patchCNPGRow(
  id: string,
  input: { approved?: boolean; eol_date?: string | null; clear_eol_date?: boolean },
): Promise<void> {
  const res = await fetch(
    `/api/admin/cnpg/catalog/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    },
  );
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
}

export async function kickCNPGSync(): Promise<CNPGSettings> {
  const res = await fetch('/api/admin/cnpg/sync', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
  return res.json();
}
